import { z } from 'zod';

import { normalizeURIComponent, TAIGA_PREFIX } from '@Common/types/b2b/axa/common';
import { ZodTaigaSavingPlanId } from '@Common/types/b2b/axa/plans';
import { ZodB2bSavingPlanCompartment } from '@Common/types/b2b/compartments';
import { ZodB2bPlanCategoryErTaiga } from '@Common/types/b2b/plans';
import { ZodId } from '@Common/types/request';
import { ZodDateTimeString } from '@Common/types/zod/zod-date-string';
import { endpoint, GetEndpointType } from '@Common/types/zod/zod-endpoint';
import { ZodTrimmedString } from '@Common/types/zod/zod-trimmed-string';

// Taiga act types whose future-flow allocation plan can be read and replaced (API190 / API280),
// only carried by the future allocation id.
// COTIS, COTI_REGUL and COTIS_EXO share the same Taiga plan (P_AFF_COT), so COTIS stands for all three.
export const FutureAllocationActTypeValues = ['COTIS'] as const;
export type FutureAllocationActType = (typeof FutureAllocationActTypeValues)[number];

export const ZodFutureAllocationId = z
  .string()
  .regex(
    new RegExp(`^${TAIGA_PREFIX}_([a-zA-Z0-9%/]+)_(${FutureAllocationActTypeValues.join('|')})$`),
    { message: 'Invalid future allocation ID format' },
  )
  .transform((futureAllocationId, ctx) => {
    try {
      return normalizeURIComponent(futureAllocationId);
    } catch {
      ctx.addIssue({
        code: 'custom',
        message: `Invalid future allocation ID: ${futureAllocationId}`,
        fatal: true,
      });
      return z.NEVER;
    }
  })
  .meta({ override: { type: 'string' } });
export type FutureAllocationId = z.infer<typeof ZodFutureAllocationId>;

export const ZodFutureAllocationLine = z.object({
  plan_id: ZodTaigaSavingPlanId,
  instrument_id: z
    .number()
    .int()
    .nullable()
    .describe('null for PILOTE plans, the fund for LIBRE plans'),
  fraction: z
    .number()
    .gt(0)
    .describe(
      'share of the future inflows, 1 = 100%; not capped, the plan is returned as Taiga holds it',
    ),
});
export type FutureAllocationLine = z.infer<typeof ZodFutureAllocationLine>;

export const ZodFutureAllocationUnmappedLine = z
  .object({
    name: ZodTrimmedString.nullable().describe(
      'fund title, or model title when the whole model is unknown',
    ),
    isin: ZodTrimmedString.nullable().describe('null when the whole model is unknown'),
    fraction: z
      .number()
      .gt(0)
      .describe(
        'share of the future inflows, 1 = 100%; not capped, the plan is returned as Taiga holds it',
      ),
  })
  .describe(
    'Part of the Taiga plan going to a fund or model unknown to Shares: shown read-only, it cannot be part of an update, so saving a new allocation replaces it',
  );
export type FutureAllocationUnmappedLine = z.infer<typeof ZodFutureAllocationUnmappedLine>;

export const ZodFutureAllocation = z.object({
  id: ZodFutureAllocationId,
  company_id: ZodId,
  plan_category: ZodB2bPlanCategoryErTaiga,
  compartment: ZodB2bSavingPlanCompartment,
  is_derogable: z
    .boolean()
    .describe(
      'the allocation can only be updated when the plan is derogable at the contract level',
    ),
  created_at: ZodDateTimeString.nullable(),
  allocations: ZodFutureAllocationLine.array(),
  unmapped_allocations: ZodFutureAllocationUnmappedLine.array(),
});
export type FutureAllocation = z.infer<typeof ZodFutureAllocation>;

export const getExploreFutureAllocationsEndpoint = endpoint({
  method: 'GET',
  pathParams: undefined,
  queryParams: undefined,
  body: undefined,
  responseBody: z.object({
    future_allocations: ZodFutureAllocation.array(),
  }),
  headers: undefined,
});

export type GetExploreFutureAllocationsEndpoint = GetEndpointType<
  typeof getExploreFutureAllocationsEndpoint
>;

export const ZodFutureAllocationUpdateBody = z.object({
  id: ZodFutureAllocationId,
  allocations: ZodFutureAllocationLine.array().min(1),
});
export type FutureAllocationUpdateBody = z.infer<typeof ZodFutureAllocationUpdateBody>;

export const ZodFutureAllocationUpdateResponse = z.object({
  is_confirmed: z
    .boolean()
    .describe(
      'true when Taiga applied the new allocation; false when Taiga rejected it, the request then being handled through a Salesforce task',
    ),
});
export type FutureAllocationUpdateResponse = z.infer<typeof ZodFutureAllocationUpdateResponse>;

export const postExploreFutureAllocationsAllocationEndpoint = endpoint({
  method: 'POST',
  pathParams: undefined,
  queryParams: undefined,
  body: ZodFutureAllocationUpdateBody,
  responseBody: ZodFutureAllocationUpdateResponse,
  headers: undefined,
});

export type PostExploreFutureAllocationsAllocationEndpoint = GetEndpointType<
  typeof postExploreFutureAllocationsAllocationEndpoint
>;
